import React from 'react';
import Button from './Button';
import AnimatedDot from './AnimatedDot';
import EyebrowPill from './EyebrowPill';

import SocialProof from './SocialProof';

// HeroSection — ZERO data dependencies, renders immediately on first paint
// Extracted to its own module so it's parsed/evaluated first before the rest of HomeAtelier
const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';

export default function HeroSection() {
  return (
    <section aria-labelledby="hero-heading" className="px-[6vw] pt-[70px] pb-[100px] overflow-hidden">
      <div className="max-w-container mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
        <header>
          <EyebrowPill variant="dark" className="mb-7">
            ikonomakis Hair Atelier
          </EyebrowPill>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-card-sm bg-gold/10 border border-gold/25 mb-5">
            <span className="text-gold text-base">🏆</span>
            <span className="text-tiny font-semibold text-warm-black leading-snug">
              Δημιουργοί του AI Hair Diagnosis · 1ο κομμωτήριο στην Ελλάδα με βραβεία τεχνολογίας
            </span>
          </div>

          <h1 id="hero-heading" className="font-serif text-display-xl text-warm-black font-medium tracking-[-3px] leading-[0.92] mb-6">
            <span className="block text-[clamp(16px,2vw,22px)] font-sans font-semibold tracking-[-0.5px] text-muted mb-2 leading-tight">Κομμωτήριο στο Χαλάνδρι</span>{' '}
            Το χρώμα που σου ταιριάζει{' '}
            <em className="italic text-gold">πραγματικά</em>.
          </h1>
          <h2 className="sr-only">
            Κομμωτήριο Χαλάνδρι — Κούρεμα &amp; Χρώμα Μαλλιών Ανδρικό &amp; Γυναικείο με Προσωπολογία &amp; Προσωπομετρία — Ikonomakis Hair Atelier
          </h2>

          <p className="text-body-l text-muted leading-[1.55] mb-6 max-w-[520px]">
            30 χρόνια τέχνης. Bespoke κούρεμα &amp; χρώμα μαλλιών — ανδρικό &amp; γυναικείο — με προσωπολογία (visagisme), προσωπομετρία και <strong className="text-warm-black font-semibold">Face Metrics Pro</strong> AI ανάλυση.
          </p>

          {/* Keyword chips — readable by AI crawlers, visually subtle */}
          <div className="flex flex-wrap gap-2 mb-8">
            <a href="/prosopologia-visagisme" className="text-tiny px-3 py-1 rounded-pill bg-cream-soft border border-brown/15 text-muted font-medium hover:border-gold hover:text-warm-black transition-colors">
              Προσωπολογία
            </a>
            {[
              'Χρώμα Μαλλιών Bespoke',
              'Ανδρικό Κούρεμα',
              'Γυναικείο Κούρεμα',
              'Προσωπομετρία',
              'Face Metrics Pro',
            ].map((chip) => (
              <span key={chip} className="text-tiny px-3 py-1 rounded-pill bg-cream-soft border border-brown/15 text-muted font-medium">
                {chip}
              </span>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-2">
            <Button variant="primary" href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Κράτηση Online →
            </Button>
            <Button variant="ghost" href="/services">
              Δες τις Υπηρεσίες
            </Button>
            {/* Live availability badge — integration point for a Fresha widget if one becomes available */}
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" aria-label="Διαθεσιμότητα αυτή την εβδομάδα — κράτηση στο Fresha">
              <EyebrowPill variant="gold-soft" withStar={false} className="hover:bg-gold/25 transition-colors">
                <AnimatedDot color="green" size={6} />
                Διαθεσιμότητα αυτή την εβδομάδα
              </EyebrowPill>
            </a>
          </div>

          <SocialProof className="mt-9 max-w-[520px]" />
        </header>

        <figure className="relative aspect-[4/5] max-h-[680px] m-0">
          <picture>
            <source
              type="image/webp"
              srcSet="
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg?width=400&format=webp 400w,
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg?width=480&format=webp 480w,
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg?format=webp 768w
              "
              sizes="(max-width: 640px) 92vw, 45vw"
            />
            <img
              src="https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg"
              srcSet="
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg?width=400 400w,
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg?width=480 480w,
                https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/8d06987ac_Image30.jpg 768w
              "
              sizes="(max-width: 640px) 92vw, 45vw"
              alt="Bespoke balayage αποτέλεσμα σε καστανά μαλλιά — Ikonomakis Hair Atelier Χαλάνδρι"
              fetchpriority="high"
              loading="eager"
              decoding="async"
              width="768"
              height="958"
              className="absolute inset-0 w-full h-full rounded-card-xl object-cover object-center"
            />
          </picture>

          <div className="absolute -bottom-6 left-3 sm:-left-6 lg:-left-10 bg-ivory rounded-card p-5 shadow-card max-w-[260px] flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center text-gold text-xl flex-shrink-0">
              ✦
            </div>
            <div>
              <div className="font-serif text-body font-medium text-warm-black mb-0.5">Bespoke Color</div>
              <div className="text-tiny text-muted">Hand-painted, signature work</div>
            </div>
          </div>

          <div className="absolute top-3 right-3 sm:-top-4 sm:-right-4 lg:-right-6 bg-warm-black text-gold-light rounded-pill px-5 py-2.5 rotate-[8deg] shadow-card flex items-center gap-2">
            <AnimatedDot color="gold" size={8} />
            <span className="text-tiny uppercase tracking-wider font-medium">AI Hair Analysis</span>
          </div>
          <figcaption className="sr-only">Bespoke balayage αποτέλεσμα σε καστανά μαλλιά — Ikonomakis Hair Atelier Χαλάνδρι</figcaption>
        </figure>
      </div>
    </section>
  );
}